import { ComponentDataDestService } from "../component-data-dest.service";
import { catchError, map, switchMap, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';

interface ApiResponse {
    data?: {
        subtype_id: number;
        [key: string]: any;
    };
    subtype_id?: string | number;
    message?: string;
    [key: string]: any;
}

interface Description {
    language_code: string;
    translation_text: string;
}

function validateDescriptions(descriptions: any): void {
    if (!descriptions || typeof descriptions !== 'object') {
        throw new Error('Descriptions must be an object');
    }
    
    for (const [lang, desc] of Object.entries(descriptions)) {
        if (!desc || typeof desc !== 'object') {
            throw new Error(`Description for language ${lang} must be an object`);
        }
        if (!('language_code' in desc) || !('translation_text' in desc)) {
            throw new Error(`Description for language ${lang} must have language_code and translation_text`);
        }
    }
}

export function add_subtype(componentDataDestService: ComponentDataDestService, componentDataSourceService: ComponentDataSourceService, data: any) {
    
    // Validate required fields
    if (!data.name || !data.visibility) {
        throw new Error('Name and visibility are required fields');
    }
    
    // Step 1: Create base subtype
    const baseSubtypeData = {
        subtype: {
            name: data.name,
            visibility: data.visibility
        }
    };

    // Send base subtype to API
    const request$ = componentDataDestService.http.post<ApiResponse>(`${environment.apiUrl}/events/subtypes`, baseSubtypeData).pipe(
        catchError(error => {
            console.error("Error creating base subtype:", error);
            throw error;
        }),
        switchMap(response => {
            
            // Extract subtype ID with defensive coding
            let subtypeId = null;
            
            
            try {
                // First try direct property which seems to be the actual format
                if (response && response.subtype_id) {
                    subtypeId = response.subtype_id;
                }
                // Then try nested under data (fallback)
                else if (response?.data?.subtype_id) {
                    subtypeId = response.data.subtype_id;
                }
            } catch (e) {
                console.error("Error accessing subtype_id from response:", e);
            }
            
            // Convert ID to number if it's a string (which appears to be the case)
            if (subtypeId && typeof subtypeId === 'string') {
                subtypeId = parseInt(subtypeId, 10);
            }
            
            if (!subtypeId) {
                console.error("Could not find subtype_id in response. This may cause updateStoreData to fail.");
                console.error("Response data:", response);
                
                // Last resort search for any id-like property
                if (typeof response === 'object' && response !== null) {
                    // Look for any property containing 'id'
                    for (const key in response) {
                        if (key.includes('id')) {
                            const possibleId = (response as any)[key];
                            subtypeId = typeof possibleId === 'string' ? parseInt(possibleId, 10) : possibleId;
                            break;
                        }
                    }
                    
                    // Check nested data property as last resort
                    if (!subtypeId && response.data) {
                        for (const key in response.data) {
                            if (key.includes('id')) {
                                const possibleId = (response.data as any)[key];
                                subtypeId = typeof possibleId === 'string' ? parseInt(possibleId, 10) : possibleId;
                                break;
                            }
                        }
                    }
                }
            }
            
            // Step 2: Create version if version data is provided
            if (data.version_start_date) {
                const startDate = new Date(data.version_start_date);
                const endDate = data.version_end_date ? new Date(data.version_end_date) : undefined;
                
                // Validate dates
                if (isNaN(startDate.getTime())) {
                    throw new Error('Invalid start date format');
                }
                if (endDate && isNaN(endDate.getTime())) {
                    throw new Error('Invalid end date format');
                }

                // Validate descriptions if provided
                if (data.descriptions) {
                    validateDescriptions(data.descriptions);
                }

                const versionData = {
                    valid_from: startDate.toISOString().split('T')[0],
                    valid_to: endDate ? endDate.toISOString().split('T')[0] : null,
                    descriptions: data.descriptions
                };

                // Create version using the subtype ID from the response
                return componentDataDestService.http.post<ApiResponse>(
                    `${environment.apiUrl}/events/subtypes/${subtypeId}/versions`,
                    versionData
                ).pipe(
                    catchError(error => {
                        console.error("Error creating subtype version:", error);
                        throw error;
                    }),
                    map(versionResponse => ({
                        ...response,
                        version: versionResponse
                    }))
                );
            }
            return of(response);
        })
    );

    // Use the helper function to handle store update
    componentDataSourceService.updateStoreData(request$, 'subtypes').subscribe({
        error: (error) => {
            console.error('Error creating subtype:', error);
            
            // Try to manually refresh the subtypes data source
            setTimeout(() => {
                const subtypesDS = componentDataSourceService.getDataSource('subtypes');
                if (subtypesDS) {
                    subtypesDS.execute().subscribe({
                        error: (err) => console.error('Manual refresh failed:', err)
                    });
                } else {
                    console.warn('Could not find subtypes data source for manual refresh');
                }
            }, 1000);
        }
    });
}